import { useCallback, type RefObject } from 'react';
import {
  buildResizeState,
  capturePointer,
  getMatchingPointerState,
  isPrimaryPointerStart,
  pinPopoverRect,
  readViewportSize,
  releasePointerIfCaptured,
  resolveResizedRect,
} from './CmarkdowntablePopoverPointerHandlers.helpers';
import type {
  CmarkdowntablePopoverRect,
  PopoverResizeState,
  TablePopoverPointerHandler,
} from './Cmarkdowntable.types';

interface UseCmarkdowntablePopoverResizeHandlersInput {
  resizeStateRef: RefObject<PopoverResizeState | null>;
  popoverRef: RefObject<HTMLDivElement | null>;
  applyManualRect: (rect: CmarkdowntablePopoverRect | null) => void;
}

interface CmarkdowntablePopoverResizeHandlers {
  handleResizePointerDown: TablePopoverPointerHandler;
  handleResizePointerMove: TablePopoverPointerHandler;
  handleResizePointerEnd: TablePopoverPointerHandler;
}

export function useCmarkdowntablePopoverResizeHandlers(
  input: UseCmarkdowntablePopoverResizeHandlersInput,
): CmarkdowntablePopoverResizeHandlers {
  const { resizeStateRef, popoverRef, applyManualRect } = input;

  const handleResizePointerDown = useCallback<TablePopoverPointerHandler>((event): void => {
    if (!isPrimaryPointerStart(event)) {
      return;
    }

    const pinned = pinPopoverRect({ popoverRef, applyManualRect });
    if (!pinned) {
      return;
    }

    resizeStateRef.current = buildResizeState({ event, pinned });
    capturePointer(event);
    event.preventDefault();
    event.stopPropagation();
  }, [applyManualRect, popoverRef, resizeStateRef]);

  const handleResizePointerMove = useCallback<TablePopoverPointerHandler>((event): void => {
    const resize = getMatchingPointerState({ stateRef: resizeStateRef, pointerId: event.pointerId });
    if (!resize) {
      return;
    }

    applyManualRect(resolveResizedRect({
      resize,
      pointerX: event.clientX,
      pointerY: event.clientY,
      viewport: readViewportSize(),
    }));
  }, [applyManualRect, resizeStateRef]);

  const handleResizePointerEnd = useCallback<TablePopoverPointerHandler>((event): void => {
    const resize = getMatchingPointerState({ stateRef: resizeStateRef, pointerId: event.pointerId });
    if (!resize) {
      return;
    }

    resizeStateRef.current = null;
    releasePointerIfCaptured(event);
  }, [resizeStateRef]);

  return {
    handleResizePointerDown,
    handleResizePointerMove,
    handleResizePointerEnd,
  };
}
